// Imports
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { createGlobalStyle } from 'styled-components/macro';
import AnchorButton from '../../../../shareables/foundation/front-end/components/buttons/anchor';
import Button from '../../../../shareables/foundation/front-end/components/buttons/standard';
import theme from '../../../../shareables/theme';
import Logo from './logo';


// Define the accepted props
interface Props extends React.PropsWithChildren {
	documentTitle: string;
	heading: string;
	description: string;
	descriptionMobileHeight?: string;
}


// Global style
const GlobalStyle = createGlobalStyle`
	body {
		margin: 0;
	}
`;


// Styled components
const Container = styled.div`
	background: #e6e6e6;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100vh;
	font-family: 'Inter';
	text-align: center;
	color: #333333;
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1.875rem;
	max-width: 40.625rem;
`;

const Heading = styled.h1`
	font-family: 'Zilla Slab';
	font-weight: 600;
	font-size: 3.75rem;
	line-height: 1;
	margin: 0;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		font-size: 2.5rem;
	}
`;

const Description = styled.p<{ mobileHeight?: string }>`
	font-size: 1.25rem;
	line-height: 1.2;
	margin: 0;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		font-size: 1rem;
		width: 20.9375rem;
		${({ mobileHeight }) => mobileHeight && `height: ${mobileHeight};`}
	}
`;

const ButtonGroup = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1.875rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		flex-direction: column;
		align-items: center;
		gap: 0.875rem;
	}
`;

export const sharedButtonStyles = `
	padding: 0.625rem 1.25rem;
	gap: 0.5rem;
	
	height: 3.375rem;
	
	background: #b3b3b3;
	border-radius: 9999rem;
	
	.svg-inline--fa {
		font-size: 1.5rem;
		color: #737373;
		
		margin-right: 0 !important;
	}
	
	span {
		font-size: 1.75rem;
		color: #333333;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		padding: 0.5rem 1rem;
		height: 2.625rem;
		
		.svg-inline--fa {
			font-size: 1.25rem;
		}
		
		span {
			font-size: 1.375rem;
		}
	}
`;

const TryAgainButton = styled(Button)`
	${sharedButtonStyles}
`;

export const SecondaryButton = styled(AnchorButton)`
	${sharedButtonStyles}
	
	box-sizing: border-box;
`;

const Footer = styled.footer`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
	bottom: 1.875rem;
	
	span {
		font-size: 0.75rem;
		line-height: 1;
		text-align: center;
		
		color: #666666;
	}
`;


// Function component
const FullPageErrorTemplate: React.FC<Props> = ({
	documentTitle,
	heading,
	description,
	descriptionMobileHeight,
	children,
}) => (
	<Container>
		<Content>
			<Helmet>
				<title>{documentTitle}</title>
				<link
					href='https://fonts.googleapis.com/css2?family=Inter:wght@400&family=Zilla+Slab:wght@600&display=swap'
					rel='stylesheet'
				/>
			</Helmet>
			
			<GlobalStyle />
			
			<Heading>{heading}</Heading>
			<Description mobileHeight={descriptionMobileHeight}>{description}</Description>
			
			<ButtonGroup>
				<TryAgainButton type='button' onClick={() => window.location.reload()} color='gray'>
					<FontAwesomeIcon icon={faArrowsRotate} /> <span>Try again</span>
				</TryAgainButton>
				
				{children}
			</ButtonGroup>
		</Content>
		
		<Footer>
			<span>Powered by</span>
			<Logo />
		</Footer>
	</Container>
);

export default FullPageErrorTemplate;
